import { Fragment, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertTriangle, Check, Minus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  findUnenforcedGrants,
  satisfiesPermission,
  UI_ENFORCED_PERMISSIONS,
} from "@/lib/permissions";
import type { Role, PermissionGroup } from "@/lib/api/admin-api";

interface PermissionMatrixProps {
  roles: Role[];
  permissionGroups: PermissionGroup[];
  defaultRoleId?: number;
}

function roleHasPermission(role: Role, resource: string, action: string): boolean {
  return satisfiesPermission(role.permissions as string[], `${resource}:${action}`);
}

/**
 * Grants the roles hold that the API does not enforce.
 *
 * The matrix can only draw rows the catalogue contains, so a role carrying a
 * permission from outside it looks — misleadingly — like a role with fewer
 * permissions than it has. Naming them here is the difference between "this
 * role can't do that" and "this role holds a string that does nothing".
 */
function DeadGrantWarning({
  roles,
  permissionGroups,
}: {
  roles: Role[];
  permissionGroups: PermissionGroup[];
}) {
  const offenders = roles
    .map((role) => ({
      role,
      dead: findUnenforcedGrants(role.permissions as string[], permissionGroups),
    }))
    .filter(({ dead }) => dead.length > 0);

  if (offenders.length === 0) return null;

  return (
    <div className="flex gap-3 rounded-lg border border-(--color-warning-text)/40 bg-(--color-warning-bg) p-3">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-(--color-warning-text)" />
      <div className="flex flex-col gap-1.5 text-xs">
        <p className="font-medium text-(--color-warning-text)">
          <Trans>
            Some roles hold permissions the API does not enforce. They grant nothing.
          </Trans>
        </p>
        {offenders.map(({ role, dead }) => (
          <p key={role.id} className="text-(--color-text-secondary)">
            <span className="font-medium text-(--color-text-primary)">{role.name}</span>{" "}
            <Trans>holds</Trans>{" "}
            {dead.map((grant, index) => (
              <Fragment key={grant}>
                {index > 0 && ", "}
                <code className="font-mono">{grant}</code>
              </Fragment>
            ))}
            .
          </p>
        ))}
        <p className="text-(--color-text-secondary)">
          <Trans>
            Restore Default Permissions clears these from the built-in roles; a custom role
            has to be edited.
          </Trans>
        </p>
      </div>
    </div>
  );
}

function FilterPills({
  options,
  selected,
  onSelect,
}: {
  options: { value: string; label: string }[];
  selected: Set<string>;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(({ value, label }) => {
        const active = selected.has(value);
        return (
          <button
            key={value}
            type="button"
            onClick={() => onSelect(value)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors capitalize ${
              active
                ? "bg-(--gray-900) text-(--brand-white) border-(--gray-900)"
                : "bg-(--color-surface-content) text-(--color-text-secondary) border-(--color-border) hover:border-(--color-border-strong)"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function PermissionMatrix({
  roles,
  permissionGroups,
  defaultRoleId,
}: PermissionMatrixProps) {
  const { t } = useLingui();
  const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set(["all"]));
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(
    () => defaultRoleId != null ? new Set([String(defaultRoleId)]) : new Set(["all"]),
  );

  if (roles.length === 0 || permissionGroups.length === 0) return null;

  const toggleFilter = (
    value: string,
    current: Set<string>,
    set: (s: Set<string>) => void,
  ) => {
    if (value === "all") {
      set(new Set(["all"]));
      return;
    }
    const next = new Set(current);
    next.delete("all");
    if (next.has(value)) {
      next.delete(value);
      if (next.size === 0) next.add("all");
    } else {
      next.add(value);
    }
    set(next);
  };

  const visibleGroups = selectedResources.has("all")
    ? permissionGroups
    : permissionGroups.filter((g) => selectedResources.has(g.resource));

  const visibleRoles = selectedRoles.has("all")
    ? roles
    : roles.filter((r) => selectedRoles.has(String(r.id)));

  const resourceOptions = [
    { value: "all", label: t`All resources` },
    ...permissionGroups.map((g) => ({ value: g.resource, label: g.resource })),
  ];

  const roleOptions = [
    { value: "all", label: t`All roles` },
    ...roles.map((r) => ({ value: String(r.id), label: r.name })),
  ];

  return (
    <div className="flex flex-col gap-4 p-4">
      <DeadGrantWarning roles={roles} permissionGroups={permissionGroups} />

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-(--color-text-secondary) uppercase tracking-wider">
            <Trans>Resources</Trans>
          </span>
          <FilterPills
            options={resourceOptions}
            selected={selectedResources}
            onSelect={(v) => toggleFilter(v, selectedResources, setSelectedResources)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-(--color-text-secondary) uppercase tracking-wider">
            <Trans>Roles</Trans>
          </span>
          <FilterPills
            options={roleOptions}
            selected={selectedRoles}
            onSelect={(v) => toggleFilter(v, selectedRoles, setSelectedRoles)}
          />
        </div>
      </div>

      {/* Matrix table. A genuine cross-tab (permissions × roles), so it uses the
          `ui/table` primitives directly rather than `DataTable`. */}
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-(--color-surface-muted) hover:bg-(--color-surface-muted)">
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary) min-w-[180px]">
                <Trans>Permission</Trans>
              </TableHead>
              {visibleRoles.map((role) => (
                <TableHead key={role.id} className="h-auto px-3 py-3 text-center font-medium min-w-[110px]">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs text-(--color-text-primary) leading-tight">{role.name}</span>
                    <Badge variant="neutral" className="text-[10px] px-1 py-0">
                      {role.isSystem ? t`system` : t`custom`}
                    </Badge>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleGroups.map((group) => (
              <Fragment key={`group-${group.resource}`}>
                <TableRow className="bg-(--color-surface-muted)/70 hover:bg-(--color-surface-muted)/70">
                  <TableCell
                    colSpan={visibleRoles.length + 1}
                    className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-(--color-text-secondary)"
                  >
                    {group.resource}
                  </TableCell>
                </TableRow>
                {group.actions.map((action) => (
                  <TableRow
                    key={`${group.resource}:${action}`}
                    className="last:border-b-0 hover:bg-(--color-surface-muted)/50"
                  >
                    <TableCell className="px-4 py-2.5 font-mono text-xs text-(--color-text-secondary)">
                      {group.resource}:{action}
                      {UI_ENFORCED_PERMISSIONS.includes(`${group.resource}:${action}`) && (
                        <span
                          className="ml-2 font-sans text-[10px] text-(--color-text-secondary)"
                          title={t`Checked by this app when deciding what to show. The API does not gate on it.`}
                        >
                          <Trans>app-enforced</Trans>
                        </span>
                      )}
                    </TableCell>
                    {visibleRoles.map((role) => {
                      const has = roleHasPermission(role, group.resource, action);

                      return (
                        <TableCell key={role.id} className="px-3 py-2.5 text-center">
                          {has ? (
                            <Check className="h-4 w-4 text-(--color-success-text) mx-auto" />
                          ) : (
                            <Minus className="h-4 w-4 text-(--gray-200) mx-auto" />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
